---
title: "Bookings, billings, revenue: stop *conflating* them."
excerpt: "Three numbers, three timelines, three owners. A shared definition that finance, sales, and the board can all read the same way."
category: "MRR & ARR"
author: alain
date: "2026-04-24"
readTime: "6 min read"
deck: "Bookings, billings, and revenue measure the same deal at three different *moments*. Treating them as one number is how forecasts go wrong."
---

Three of the most important numbers in a subscription business are routinely used as if
they were interchangeable. Bookings, billings, and revenue describe the same contract at
three different points in its life, and conflating them produces forecasts that don't
reconcile and board conversations that talk past each other. The fix isn't more
sophistication. It's a shared definition everyone agrees to.

## Three moments, not three opinions

Each number answers a different question, and each is owned by a different part of the
business:

- **Bookings** — the total value a customer committed to when they signed. Sales owns
  it. It's a promise, recognized the moment the contract is signed.
- **Billings** — what you've actually invoiced so far. Finance owns it. It follows the
  invoicing schedule, which may be monthly, annual, or milestone-based.
- **Revenue** — what you've earned by delivering the service over time. Accounting owns
  it. It's recognized ratably as the obligation is fulfilled.

A single annual deal can be a large booking today, a single billing this month, and a
small slice of recognized revenue this period, all from the same signature.

## Why conflating them breaks the forecast

When a team reports bookings as if they were revenue, growth looks lumpy and front-loaded.
When it reports billings as revenue, seasonality in invoicing masquerades as business
performance. The numbers are all correct; they're just answering different questions, and
mixing them produces a story that falls apart the first time someone reconciles it.

> Bookings is what you sold. Billings is what you invoiced. Revenue is what you earned.
> The discipline is never using one word to mean another.

## A definition the whole company can read

The goal is one definition, written down, that finance, sales, and the board all use the
same way, plus the ability to move between the three on demand. When the same underlying
contracts drive all three views, the bridge between them stops being a debate and becomes
a report.
